import { PayloadAction } from '@reduxjs/toolkit';

import { PatientHealthConcernDto, PatientHealthConcernSaveRequest, PatientHealthConcernsClient } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

// A save either succeeds (list refetched) or comes back with a readable message for the modal to show inline.
// A stale rowVersion arrives as a succeeded:false envelope, but the save gate's own rules are thrown: the
// server raises MedOneValidationException, which the API maps to a 400 ProblemDetails, so the catch has to
// read them out.
export type SaveHealthConcernResult = { succeeded: boolean; message?: string };

// SCRUM-1414 - the stale-patient guard reads census.currentCensus, which only the chart maintains. Census-less
// routes (the Clinical Coordinator Medical Summary) can still have it pinned to a patient edited earlier in the
// shift, so their health concerns would be discarded as another patient's; they opt out. The ownership tag on
// the list is what keeps that safe: the panel renders rows only while they are tagged to the patient it is
// showing.
type HealthConcernGuardOptions = { bypassActivePatientGuard?: boolean };

const shouldDiscard = (state: RootState, patientId: number, options?: HealthConcernGuardOptions) => !options?.bypassActivePatientGuard && shouldSkipByPatientId(state.census, patientId);

export const reducers = {
    setPatientHealthConcernsLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientHealthConcernsLoading = action.payload;
    },

    // The list and the patient it belongs to are written together so they can never disagree: a caller cannot
    // store rows and forget to say whose they are.
    setPatientHealthConcerns: (state: CensusState, action: PayloadAction<{ patientId: number; concerns: PatientHealthConcernDto[] }>) => {
        state.patientHealthConcerns = action.payload.concerns;
        state.patientHealthConcernsPatientId = action.payload.patientId;
    },
};

// SCRUM-1414 — health concerns live in the census slice (not a panel-local hook) because the chart drawer and
// the Medical Summary form both render the panel, and two copies of the list could disagree after a write.
export function fetchPatientHealthConcerns(patientId: number, options?: HealthConcernGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(censusSlice.actions.setPatientHealthConcernsLoading(true));
        // SCRUM-1414 - both mount points read this one list, so ANOTHER patient's rows have to go before the
        // new fetch resolves; the loading mask is translucent and they would stay readable underneath it. Rows
        // that already belong to this patient stay put: every save and delete awaits a refetch, and blanking
        // those flashes "no health concerns documented" on the chart, the same misread on the common path.
        if (getState().census.patientHealthConcernsPatientId !== patientId) {
            dispatch(censusSlice.actions.setPatientHealthConcerns({ patientId, concerns: [] }));
        }

        const client = new PatientHealthConcernsClient(null, Axios);

        try {
            const response = await client.getByPatient(patientId);

            // Guard against race condition: discard if user navigated to a different patient.
            if (shouldDiscard(getState(), patientId, options)) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setPatientHealthConcerns({ patientId, concerns: response.result.entity ?? [] }));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            // In a finally, not after the try: the race guard returns early, and a discarded fetch that never
            // cleared the flag would leave the table spinning for good.
            dispatch(censusSlice.actions.setPatientHealthConcernsLoading(false));
        }
    };
}

// concernId null = create, otherwise update. Refetches on success so the server-resolved code system lands.
export function savePatientHealthConcern(
    patientId: number,
    concernId: number | null,
    request: PatientHealthConcernSaveRequest,
    options?: HealthConcernGuardOptions
): AppThunk<Promise<SaveHealthConcernResult>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientHealthConcernsClient(null, Axios);

        try {
            const response = concernId != null ? await client.update(patientId, concernId, request) : await client.create(patientId, request);

            if (response.result.succeeded) {
                // Skip the refetch if the user has since navigated to another patient.
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientHealthConcerns(patientId, options));
                }

                return { succeeded: true };
            }

            return { succeeded: false, message: response.result.message };
        } catch (error) {
            // Only a 400 carries the save gate's own wording; a 500's detail is server internals, not a rule a
            // clinician can act on, so it stays behind the generic message.
            const validationDetail = error?.status === 400 ? error?.response?.detail : null;

            // The modal surfaces this inline; don't also toast via handleError, or a thrown save (e.g. a dropped
            // connection) would report the same failure twice.
            return { succeeded: false, message: validationDetail || 'Something went wrong saving the health concern.' };
        }
    };
}

// Removal has no inline surface — the row's Popconfirm closes either way — so a failure toasts.
export function removePatientHealthConcern(patientId: number, concernId: number, options?: HealthConcernGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientHealthConcernsClient(null, Axios);

        try {
            // Note the client method name: deleteConcern, not deleteGoal.
            const response = await client.deleteConcern(patientId, concernId);

            if (response.result.succeeded) {
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientHealthConcerns(patientId, options));
                }
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

// One shared instance, never an inline []: a fresh array on every render would break the selector's referential
// check and re-render the panel on every unrelated census change.
const NO_CONCERNS: PatientHealthConcernDto[] = [];

// SCRUM-1414 - the list and its ownership tag are read together, so there is no ungated way to render the rows.
// The fetch that replaces them is dispatched from an effect, which React commits AFTER the render, so reading
// the list alone paints the previous patient's coded concept and clinical text under the new patient's header.
export const selectPatientHealthConcernsFor = (patientId?: number) => (state: RootState) =>
    state.census.patientHealthConcernsPatientId === patientId ? state.census.patientHealthConcerns : NO_CONCERNS;

export const selectPatientHealthConcernsLoading = (state: RootState) => state.census.patientHealthConcernsLoading;
